import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Tab, Nav, ProgressBar, Row, Col, Alert, Card, Badge } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import axios from 'axios';
import api from '../services/api';
import Swal from 'sweetalert2';

const VisitControlModal = ({ show, onHide, appointment, onComplete }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [reports, setReports] = useState([]); // Array of { file, preview, name, type }
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [notes, setNotes] = useState('');
    const [notifyingPatient, setNotifyingPatient] = useState(false);
    const [notifyingDoctor, setNotifyingDoctor] = useState(false);
    const [vitals, setVitals] = useState(null);

    // Fetch existing analysis if any
    useEffect(() => {
        if (show && appointment) {
            fetchAnalysis();
            fetchVitals();
        } else {
            // Reset states on hide
            setNotes('');
            setReports([]);
            setUploadProgress(0);
            setActiveTab('overview');
            setVitals(null);
        }
    }, [show, appointment]);

    if (!appointment) return null;

    const fetchAnalysis = async () => {
        try {
            const res = await api.get(`/assistant/analysis/${appointment._id}`);
            if (res.data) {
                setNotes(res.data.notes || '');
                if (res.data.reports && Array.isArray(res.data.reports)) {
                    // Map existing reports to the expected format
                    setReports(res.data.reports.map(r => ({
                        preview: r.url,
                        name: r.name || 'Previous Report',
                        type: r.type,
                        existing: true
                    })));
                } else if (res.data.prescriptionUrl) {
                    // Backward compatibility
                    setReports([{ preview: res.data.prescriptionUrl, name: 'Uploaded Rx', type: 'image', existing: true }]);
                }
            }
        } catch (error) {
            console.error("Error fetching analysis:", error);
        }
    };

    const fetchVitals = async () => {
        try {
            const res = await api.get(`/vitals/appointment/${appointment._id}`);
            if (res.data && res.data.vitals) {
                setVitals(res.data);
            }
        } catch (error) {
            console.error("Error fetching vitals:", error);
        }
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            setUploading(true);
            setUploadProgress(0);

            let processed = 0;
            const newReports = [];

            files.forEach(file => {
                if (file.size > 5 * 1024 * 1024) {
                    Swal.fire('Error', `File ${file.name} is too large (Max 5MB)`, 'error');
                    return;
                }

                const reader = new FileReader();
                reader.onloadend = () => {
                    newReports.push({
                        file: file,
                        preview: reader.result,
                        name: file.name,
                        type: file.type
                    });
                    processed++;

                    if (processed === files.length) {
                        setReports(prev => [...prev, ...newReports]);
                        setUploading(false);
                        setUploadProgress(100);
                    }
                };
                reader.readAsDataURL(file);
            });
        }
    };

    const removeReport = (index) => {
        setReports(prev => prev.filter((_, i) => i !== index));
    };

    const handleSendToDoctor = async () => {
        try {
            // Save Analysis
            await api.post('/assistant/analysis', {
                appointmentId: appointment._id,
                notes: notes,
                reports: reports.map(r => ({
                    url: r.preview, // Base64
                    name: r.name,
                    type: r.type
                }))
            });
            Swal.fire('Sent!', 'Analysis and reports sent to Doctor successfully.', 'success');
        } catch (error) {
            console.error("Send Analysis Error:", error);
            Swal.fire('Error', 'Failed to send analysis', 'error');
        }
    };

    const handleNotifyPatient = async () => {
        setNotifyingPatient(true);
        try {
            await api.post(`/assistant/notify-patient/${appointment._id}`, {});
            Swal.fire({
                title: 'Notified!',
                text: 'Patient has been notified via email.',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Failed to notify patient', 'error');
        } finally {
            setNotifyingPatient(false);
        }
    };

    const handleNotifyDoctor = async () => {
        setNotifyingDoctor(true);
        try {
            await api.post(`/assistant/notify-doctor/${appointment._id}`, {});
            Swal.fire({
                title: 'Doctor Notified',
                text: 'A high-priority notification sent to the doctor.',
                icon: 'info',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            Swal.fire('Error', 'Failed to notify doctor', 'error');
        } finally {
            setNotifyingDoctor(false);
        }
    };

    const handleFinish = async () => {
        try {
            // 1. Save Analysis first (Auto-save on finish)
            await api.post('/assistant/analysis', {
                appointmentId: appointment._id,
                notes: notes,
                reports: reports.map(r => ({
                    url: r.preview,
                    name: r.name,
                    type: r.type
                }))
            });

            // 2. Complete the visit
            onComplete(appointment._id);
            onHide();
            Swal.fire('Visit Completed', 'Assistant analysis and prescription saved.', 'success');
        } catch (error) {
            console.error("Finish Visit Error:", error);
            Swal.fire('Error', 'Failed to save visit data', 'error');
        }
    };

    const handleViewFile = (url) => {
        if (!url) return;
        const win = window.open();
        if (win) {
            win.document.write(`<img src="${url}" style="width:100%; height:auto;" />`);
            win.document.title = "View File";
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered backdrop="static" className="glass-modal">
            <Modal.Header closeButton className="border-0 bg-light">
                <Modal.Title className="d-flex align-items-center gap-2">
                    <div className="bg-primary text-white rounded-circle p-2 d-flex align-items-center justify-content-center shadow-sm" style={{ width: 44, height: 44 }}>
                        <span className="fw-bold fs-5">{appointment.queueNumber || '#'}</span>
                    </div>
                    <div>
                        <h5 className="mb-0 fw-bold text-dark">{appointment.guestName || appointment.patientId?.name || 'Guest Patient'}</h5>
                        <div className="d-flex align-items-center gap-2">
                            <Badge bg="info-subtle" text="info" className="fw-medium border border-info-subtle">Check-in Analysis</Badge>
                            <small className="text-muted fw-normal">{appointment.timeSlot}</small>
                        </div>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0">
                <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                    <Row className="g-0">
                        <Col md={3} className="bg-light border-end">
                            <Nav variant="pills" className="flex-column p-3 gap-2 visit-nav">
                                <Nav.Item>
                                    <Nav.Link eventKey="overview" className="d-flex align-items-center gap-3 py-3 px-4 rounded-3 transition-all">
                                        <Icon icon="solar:user-id-bold-duotone" className="fs-4" /> Overview
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="rx" className="d-flex align-items-center gap-3 py-3 px-4 rounded-3 transition-all">
                                        <Icon icon="solar:folder-with-files-bold-duotone" className="fs-4" /> Previous Reports
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="notes" className="d-flex align-items-center gap-3 py-3 px-4 rounded-3 transition-all">
                                        <Icon icon="solar:notebook-bold-duotone" className="fs-4" /> Observations
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>
                        </Col>
                        <Col md={9} className="p-4 bg-white">
                            <Tab.Content>
                                <Tab.Pane eventKey="overview">
                                    <div className="mb-4">
                                        <h6 className="fw-bold mb-3 text-secondary d-flex align-items-center gap-2">
                                            <Icon icon="solar:bolt-bold-duotone" className="text-primary" />
                                            Communication Center
                                        </h6>
                                        <div className="d-flex gap-3">
                                            <Button
                                                variant="primary"
                                                className="flex-fill py-4 d-flex flex-column align-items-center gap-2 border-0 shadow-sm hover-translate-up transition-all"
                                                onClick={handleNotifyPatient}
                                                disabled={notifyingPatient}
                                            >
                                                {notifyingPatient ? <ProgressBar animated now={100} className="w-100" style={{ height: '4px' }} /> : (
                                                    <>
                                                        <Icon icon="solar:letter-bold-duotone" className="text-4xl" />
                                                        <div className="fw-bold">Notify Patient</div>
                                                        <small className="opacity-75">Send Active Email</small>
                                                    </>
                                                )}
                                            </Button>
                                            <Button
                                                variant="danger"
                                                className="flex-fill py-4 d-flex flex-column align-items-center gap-2 border-0 shadow-sm hover-translate-up transition-all"
                                                onClick={handleNotifyDoctor}
                                                disabled={notifyingDoctor}
                                            >
                                                <Icon icon="solar:bell-bing-bold-duotone" className="text-4xl" />
                                                <div className="fw-bold">Notify Doctor</div>
                                                <small className="opacity-75">Direct Alert</small>
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="text-center py-3 bg-light-subtle rounded-4 border border-dashed">
                                        <div className="text-xs text-secondary text-uppercase fw-bold ls-1 mb-1">Arrival Log</div>
                                        <div className="fw-bold text-dark fs-5">
                                            Arrived at <span className="text-primary">{appointment.timeSlot}</span>
                                        </div>
                                    </div>
                                </Tab.Pane>

                                <Tab.Pane eventKey="rx">

                                    <div className="text-center p-4 border-2 rounded-4 border-dashed bg-light-subtle hover-bg-light transition-all mb-4">
                                        <input type="file" id="report-upload" className="d-none" onChange={handleFileUpload} accept="image/*,application/pdf" multiple />
                                        <label htmlFor="report-upload" className="cursor-pointer w-100">
                                            <div className="mb-2 text-primary opacity-50">
                                                <Icon icon="solar:upload-track-bold-duotone" className="text-6xl" />
                                            </div>
                                            <h6 className="fw-bold mb-1">Upload Previous Reports</h6>
                                            <p className="text-secondary text-xs mb-0">Images or PDF (Max 5MB each)</p>
                                        </label>
                                    </div>

                                    {reports.length > 0 && (
                                        <div className="d-flex flex-column gap-2">
                                            <h6 className="text-xs fw-bold text-uppercase text-secondary ls-1 mb-2">Attached Files ({reports.length})</h6>
                                            {reports.map((report, idx) => (
                                                <div key={idx} className="bg-white p-2 rounded-3 border d-flex align-items-center gap-3 shadow-sm">
                                                    <div className="bg-light p-2 rounded">
                                                        {report.type?.includes('pdf') ?
                                                            <Icon icon="solar:file-text-bold-duotone" className="text-danger fs-4" /> :
                                                            <Icon icon="solar:gallery-bold-duotone" className="text-primary fs-4" />
                                                        }
                                                    </div>
                                                    <div className="flex-fill overflow-hidden">
                                                        <div className="text-sm fw-medium text-truncate">{report.name}</div>
                                                        <a href="#" className="text-xs text-primary text-decoration-none" onClick={(e) => { e.preventDefault(); handleViewFile(report.preview); }}>View File</a>
                                                    </div>
                                                    <Button variant="danger" size="sm" className="rounded-circle p-1" onClick={() => removeReport(idx)}>
                                                        <Icon icon="solar:trash-bin-trash-bold" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {uploading && (
                                        <div className="mt-3 px-2">
                                            <div className="d-flex justify-content-between mb-1">
                                                <small className="fw-bold text-primary">Uploading...</small>
                                            </div>
                                            <ProgressBar animated now={100} className="rounded-pill" style={{ height: '6px' }} />
                                        </div>
                                    )}
                                </Tab.Pane>

                                <Tab.Pane eventKey="notes">
                                    <h6 className="fw-bold mb-3 text-secondary d-flex align-items-center gap-2">
                                        <Icon icon="solar:document-text-bold-duotone" className="text-primary" />
                                        Assistant Observations
                                    </h6>

                                    {vitals && vitals.vitals && (
                                        <div className="mb-3 p-3 bg-white border rounded-3 shadow-sm">
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <h6 className="text-xs fw-bold text-uppercase text-primary ls-1 mb-0">Recorded Vitals</h6>
                                                <Badge bg="success-subtle" text="success" className="text-xs">Saved</Badge>
                                            </div>
                                            <div className="d-flex flex-wrap gap-x-4 gap-y-2 text-sm">
                                                {vitals.vitals.bp && <div><span className="text-secondary me-1">BP:</span><span className="fw-bold text-dark">{vitals.vitals.bp}</span></div>}
                                                {vitals.vitals.temperature && <div><span className="text-secondary me-1">Temp:</span><span className="fw-bold text-dark">{vitals.vitals.temperature}</span></div>}
                                                {vitals.vitals.pulse && <div><span className="text-secondary me-1">Pulse:</span><span className="fw-bold text-dark">{vitals.vitals.pulse} bpm</span></div>}
                                                {vitals.vitals.spo2 && <div><span className="text-secondary me-1">SpO2:</span><span className="fw-bold text-dark">{vitals.vitals.spo2}%</span></div>}
                                                {vitals.vitals.weight && <div><span className="text-secondary me-1">Weight:</span><span className="fw-bold text-dark">{vitals.vitals.weight} kg</span></div>}
                                            </div>
                                            {vitals.notes && (
                                                <div className="mt-2 pt-2 border-top">
                                                    <span className="text-secondary text-xs">Note:</span> <span className="text-sm fst-italic">{vitals.notes}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <Form.Group className="mb-0">
                                        <Form.Control
                                            as="textarea"
                                            rows={8}
                                            className="border-2 rounded-4 p-3 bg-light-subtle focus-bg-white shadow-none transition-all"
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            placeholder="Mention specific symptoms, previous complaints, or vitals for the doctor's review..."
                                            style={{ fontSize: '0.95rem' }}
                                        />
                                    </Form.Group>
                                    <div className="mt-2 text-xs text-secondary-light ms-2">
                                        <Icon icon="solar:info-circle-bold" className="me-1" />
                                        These notes will be pinned to the doctor's dashboard.
                                    </div>
                                </Tab.Pane>
                            </Tab.Content>
                        </Col>
                    </Row>
                </Tab.Container>
            </Modal.Body>
            <Modal.Footer className="border-0 bg-light p-3">
                <Button variant="link" className="text-secondary text-decoration-none fw-medium me-auto" onClick={onHide}>Close</Button>
                <Button variant="info" className="text-white px-4 py-2 fw-medium rounded-3 shadow-sm d-flex align-items-center gap-2" onClick={handleSendToDoctor}>
                    <Icon icon="solar:plain-2-bold-duotone" /> Send to Doctor
                </Button>
                <Button variant="success" className="px-5 py-2 fw-bold rounded-3 shadow-sm d-flex align-items-center gap-2" onClick={handleFinish}>
                    Complete & Forward <Icon icon="solar:arrow-right-up-bold" className="text-lg" />
                </Button>
            </Modal.Footer>

            <style>{`
                .visit-nav .nav-link { color: #64748b; font-weight: 500; border: none !important; }
                .visit-nav .nav-link.active { background: #eff6ff !important; color: #2563eb !important; border-right: 4px solid #2563eb !important; border-radius: 0 8px 8px 0 !important; }
                .visit-nav .nav-link:hover:not(.active) { background: #f1f5f9; color: #0f172a; }
                .transition-all { transition: all 0.2s ease; }
                .hover-shadow:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.08); transform: translateY(-2px); }
                .ls-1 { letter-spacing: 0.05em; }
                .border-dashed { border-style: dashed !important; }
            `}</style>
        </Modal>
    );
};

export default VisitControlModal;
